import { PreviewMessageType } from '@webcontainer/api';
import stripAnsi from 'strip-ansi';
import { chatStore } from '~/lib/stores/chat';
import type {
  PotentialPreviewProblem,
  PotentialProblem,
  PotentialShellProblem,
  PotentialSupabaseFunctionProblem,
  PotentialSupabaseMigrationProblem,
  PotentialTerminalProblem,
} from '~/types/chat';
import { logger } from '~/utils/logger';
import { plural } from '~/utils/plural';
import { stripIndents } from '~/utils/stripIndent';
import { applyControlCharacters } from '~/utils/terminal';

export function renderProblems(problems: PotentialProblem[], userContext?: string) {
  const promptMode = chatStore.promptMode.get();
  const isDiscussionMode = promptMode === 'discussion';
  const errorPlural = plural(problems.length, 'error', 'errors');

  const intro = [
    `${plural(problems.length, 'An error has', 'Errors have')} occured while running the program.`,
    isDiscussionMode
      ? `I want you to help me debug ${plural(problems.length, 'this error', 'these errors')}. Please analyze the issue and suggest a solution.`
      : 'I want you to create an artifact that resolves these errors.',
    `Only fix the ${errorPlural} I describe, do not do anything else.`,
    `If you don't have enough information to fix the ${errorPlural}, ${
      isDiscussionMode
        ? 'inform the user that you need more information on the problem'
        : 'do not create an artifact and inform the user that you need more information on the problem'
    }.`,
  ].join('\n\n');

  let prompt = intro + '\n\n' + renderProblemList(problems);

  if (userContext) {
    prompt += `\n\nThe user has provided additional context about the ${plural(problems.length, 'error')}: ${userContext}`;
  }

  return prompt;
}

function renderProblemList(problems: PotentialProblem[]) {
  const binned = Object.entries(binProblems(problems));

  return binned
    .map(([type, problems]) => {
      if (type === 'terminal') {
        return renderTerminalProblems(problems as PotentialTerminalProblem[]);
      } else if (type === 'shell') {
        return renderShellProblems(problems as PotentialShellProblem[]);
      } else if (type === 'supabase-migration') {
        return renderSupabaseMigrationProblems(problems as PotentialSupabaseMigrationProblem[]);
      } else if (type === 'supabase-function') {
        return renderSupabaseFunctionProblems(problems as PotentialSupabaseFunctionProblem[]);
      } else if (type === 'preview') {
        return renderPreviewProblems(problems as PotentialPreviewProblem[]);
      }

      logger.error(`Invalid error type ${type}`);

      return '';
    })
    .join('\n\n');
}

function renderTerminalProblems(problems: PotentialTerminalProblem[]) {
  const intro = stripIndents`
    Terminal Errors:

    ${plural(problems.length, 'This error occurs', 'These errors occur')} in the terminal where we install dependencies and run tooling for the project.

    The user also uses this terminal to run their own commands.
  `;

  return (
    intro +
    '\n\n' +
    problems
      .map((problem) => `Error message: ${stripAnsi(applyControlCharacters(problem.output)).slice(0, 800)}`)
      .join('\n\n')
  );
}

function renderShellProblems(problems: PotentialShellProblem[]) {
  const intro = stripIndents`
    Shell Errors:

    ${plural(problems.length, 'This error comes', 'These errors come')} from shell commands that have been run by the AI agent, rather than the user.`;

  return (
    intro +
    '\n\n' +
    problems
      .map((problem) =>
        `
Command: ${problem.command.slice(0, 800)}
Exit Code: ${problem.exitCode ?? 0}
Error message: ${stripAnsi(problem.output).slice(0, 800)}
  `.trim(),
      )
      .join('\n\n')
  );
}

function renderSupabaseFunctionProblems(problems: PotentialSupabaseFunctionProblem[]) {
  const intro = stripIndents`
    Supabase Edge Function Errors:

    ${plural(problems.length, 'This error occurs', 'These errors occur')} when executing a Supabase Edge Function.
  `;

  return (
    intro +
    '\n\n' +
    problems
      .map((problem) =>
        `
Function name: ${problem.functionSlug}
Error message: ${problem.message.slice(0, 800)}
${problem.logs ? `Logs: ${problem.logs.join('\n').slice(0, 800)}` : 'No logs usually indicate a CORS error.'}
  `.trim(),
      )
      .join('\n\n')
  );
}

function renderSupabaseMigrationProblems(problems: PotentialSupabaseMigrationProblem[]) {
  const intro = stripIndents`
    Supabase Migration Errors:

    ${plural(problems.length, 'This error occurs', 'These errors occur')} when migrating a supabase database.
  `;

  return (
    intro +
    '\n\n' +
    problems
      .map((problem) =>
        `
Query: ${problem.query.slice(0, 800)}
File path: ${problem.filePath}
Error message: ${problem.message.slice(0, 800)}
  `.trim(),
      )
      .join('\n\n')
  );
}

function renderPreviewProblems(problems: PotentialPreviewProblem[]) {
  const intro = stripIndents`
    Browser Errors:

    ${plural(problems.length, 'This error comes', 'These errors come')} from the frontend, in the web browser viewing a webpage from the app.
  `;

  return (
    intro +
    '\n\n' +
    problems
      .map(({ error }) => {
        const url = new URL('https://localhost');

        if (error.port) {
          url.port = String(error.port);
        }

        url.hash = error.hash;
        url.pathname = error.pathname;
        url.search = error.search;

        if (error.type === PreviewMessageType.ConsoleError) {
          const message = error.args
            .map((arg) => {
              if (typeof arg === 'string') {
                return arg;
              } else if (typeof arg?.message === 'string') {
                return arg.message;
              } else {
                return JSON.stringify(arg);
              }
            })
            .join('\n\n');

          return `
URL: ${url.toString().slice(0, 800)}
Error message: ${message.slice(0, 800)}
Error stack: ${error.stack.trim().slice(0, 1000)}
      `.trim();
        }

        return `
URL: ${url.toString().slice(0, 800)}
Error message: ${error.message.slice(0, 800)}
Error stack: ${error.stack?.trim().slice(0, 1000)}
    `.trim();
      })
      .join('\n\n')
  );
}

type BinnedProblems = Record<PotentialProblem['type'], PotentialProblem[]>;

function binProblems(problems: PotentialProblem[]): BinnedProblems {
  return problems.reduce((bins, item) => {
    if (!bins[item.type]) {
      bins[item.type] = [];
    }

    bins[item.type].push(item);

    return bins;
  }, {} as BinnedProblems);
}
